import React, { useState, useEffect, useCallback } from 'react';
import { userAPI, orderAPI, refundAPI, merchantAPI, disputeAPI, settingsAPI } from '../../services/api';
import { FiUser, FiPhone, FiMail, FiMapPin, FiGlobe, FiTruck, FiPackage, FiCheck, FiCheckCircle, FiClock, FiDownloadCloud, FiShield, FiX, FiAlertCircle } from 'react-icons/fi';
import Loading from '../../components/common/Loading';
import DeliveryTimeline from '../../components/DeliveryTimeline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';

// Helpers images
import { getPlaceholderImage } from '../../utils/imageUtils';
import { getDealImageUrl } from '../../utils/dealImageHelper';

// 4.4 — Anti-désintermédiation : coordonnées du partenaire révélées UNIQUEMENT
// après un achat payé (à la demande, via l'endpoint réservé /merchants/:id/contact).
const fmtAddr = (a) => {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return [a.label, a.street, a.address, a.city, a.region, a.country]
    .filter(Boolean).join(', ');
};

// Livraison (produits physiques)
const isPhysicalOrder = (order) =>
  (order.items || []).some((it) => ((it.deal && it.deal.productType) || it.productType) === 'physical');
const isDigitalOrder = (order) =>
  (order.items || []).some((it) => ((it.deal && it.deal.productType) || it.productType) === 'digital');

const DELIVERY_META = {
  pending: { label: 'En préparation', cls: 'bg-gray-100 text-gray-600', Icon: FiClock },
  accepted: { label: 'Acceptée', cls: 'bg-blue-50 text-blue-700', Icon: FiCheck },
  prepared: { label: 'Préparée', cls: 'bg-indigo-50 text-indigo-700', Icon: FiPackage },
  shipped: { label: 'Expédiée', cls: 'bg-violet-50 text-violet-700', Icon: FiTruck },
  delivered: { label: 'Livrée', cls: 'bg-amber-50 text-amber-700', Icon: FiTruck },
  received: { label: 'Réceptionnée', cls: 'bg-green-50 text-green-700', Icon: FiCheckCircle }
};

const DeliveryBadge = ({ status }) => {
  const m = DELIVERY_META[status] || DELIVERY_META.pending;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${m.cls}`}>
      <Icon size={13} /> {m.label}
    </span>
  );
};

const MerchantContactReveal = ({ order }) => {
  const paid = String(order.paymentStatus || '').toLowerCase() === 'completed';
  const merchantId = order.merchantId
    || (order.items && order.items[0] && order.items[0].merchantId)
    || null;
  const [open, setOpen] = useState(false);
  const [contact, setContact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (!paid || !merchantId) return null;

  const toggle = async () => {
    if (contact) { setOpen((v) => !v); return; }
    setBusy(true); setErr('');
    try {
      const res = await merchantAPI.getContact(merchantId);
      setContact(res.data?.data || null);
      setOpen(true);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Coordonnées indisponibles pour le moment.');
    } finally { setBusy(false); }
  };

  const addr = contact && (contact.legalAddress
    || (Array.isArray(contact.addresses) && contact.addresses.length ? fmtAddr(contact.addresses[0]) : ''));

  return (
    <div className="border-t pt-4 mt-3">
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:underline disabled:opacity-60"
      >
        <FiUser className="shrink-0" />
        {busy
          ? 'Chargement…'
          : (open ? 'Masquer les coordonnées du partenaire' : 'Voir les coordonnées du partenaire')}
      </button>

      {err && <p className="text-sm text-red-600 mt-2">{err}</p>}

      {open && contact && (
        <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm space-y-2">
          {contact.businessName && (
            <p className="font-semibold text-gray-900">{contact.businessName}</p>
          )}
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-gray-700 hover:text-primary-600">
              <FiPhone className="shrink-0" /> {contact.phone}
            </a>
          )}
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-gray-700 hover:text-primary-600">
              <FiMail className="shrink-0" /> {contact.email}
            </a>
          )}
          {addr && (
            <p className="flex items-start gap-2 text-gray-700">
              <FiMapPin className="shrink-0 mt-0.5" /> <span>{addr}</span>
            </p>
          )}
          {contact.website && (
            <a
              href={String(contact.website).startsWith('http') ? contact.website : `https://${contact.website}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-700 hover:text-primary-600"
            >
              <FiGlobe className="shrink-0" /> {contact.website}
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  // P1-C : demande de remboursement
  const [refundFor, setRefundFor] = useState(null); // commande ciblée par la modal
  const [refundReason, setRefundReason] = useState('other');
  const [refundDesc, setRefundDesc] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);
  const REFUND_REASONS = [
    { value: 'not_delivered', label: 'Non livré / prestation non honorée' },
    { value: 'wrong_item', label: 'Article ou prestation différente' },
    { value: 'quality_issue', label: 'Problème de qualité' },
    { value: 'other', label: 'Autre' }
  ];
  const REFUND_STATUS = {
    pending: { label: 'Remboursement : en attente', cls: 'bg-amber-100 text-amber-800' },
    approved: { label: 'Remboursement : approuvé', cls: 'bg-green-100 text-green-800' },
    processed: { label: 'Remboursement : traité', cls: 'bg-green-100 text-green-800' },
    rejected: { label: 'Remboursement : refusé', cls: 'bg-red-100 text-red-700' }
  };
  const submitRefund = async () => {
    if (!refundFor) return;
    setRefundBusy(true);
    try {
      const r = await refundAPI.request(refundFor.id || refundFor._id, { reason: refundReason, description: refundDesc });
      toast.success(r?.data?.message || 'Demande envoyée');
      setRefundFor(null); setRefundReason('other'); setRefundDesc('');
      loadOrders();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur lors de l'envoi");
    } finally { setRefundBusy(false); }
  };

  // ===== Litiges / réclamations =====
  const [disputeFor, setDisputeFor] = useState(null);
  const [disputeReason, setDisputeReason] = useState('not_received');
  const [disputeDesc, setDisputeDesc] = useState('');
  const [disputeBusy, setDisputeBusy] = useState(false);
  const DISPUTE_REASONS = [
    { value: 'not_received', label: 'Non reçu' },
    { value: 'damaged', label: 'Endommagé' },
    { value: 'not_as_described', label: 'Non conforme à la description' },
    { value: 'other', label: 'Autre' }
  ];
  const DISPUTE_STATUS = {
    open: { label: 'Réclamation : ouverte', cls: 'bg-amber-100 text-amber-800' },
    in_review: { label: 'Réclamation : en cours d\'examen', cls: 'bg-blue-100 text-blue-800' },
    resolved: { label: 'Réclamation : résolue', cls: 'bg-green-100 text-green-800' },
    rejected: { label: 'Réclamation : rejetée', cls: 'bg-red-100 text-red-700' }
  };
  const submitDispute = async () => {
    if (!disputeFor) return;
    setDisputeBusy(true);
    try {
      const r = await disputeAPI.open(disputeFor.id || disputeFor._id, { reason: disputeReason, description: disputeDesc });
      toast.success(r?.data?.message || 'Réclamation enregistrée');
      setDisputeFor(null); setDisputeReason('not_received'); setDisputeDesc('');
      loadOrders();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur lors de l'envoi de la réclamation");
    } finally { setDisputeBusy(false); }
  };

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userAPI.getOrders();
      const payload = res?.data?.data ?? res?.data ?? [];
      setOrders(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
      toast.error('Impossible de charger les commandes.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const [proofBusy, setProofBusy] = useState(null);
  // ===== Lot B1 — Accompagnement de l'attente virement =====
  // Délai de validation annoncé à l'acheteur (modifiable).
  const VALIDATION_DELAY = '24 à 48 h';
  const [bankInfo, setBankInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    settingsAPI.get()
      .then((r) => { const s = (r && r.data && r.data.data) || (r && r.data) || {}; if (alive) setBankInfo(s.bankTransfer || (s.settings && s.settings.bankTransfer) || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const [recvBusy, setRecvBusy] = useState(null);
  const [confirmRecvFor, setConfirmRecvFor] = useState(null); // commande en attente de confirmation (modale)

  const openDigital = async (order) => {
    const id = order.id || order._id;
    try {
      await orderAPI.logDigitalAccess(id);
    } catch (e) { /* l'ouverture ne doit pas être bloquée par l'échec du log */ }
    if (order.digitalLink) window.open(order.digitalLink, '_blank', 'noopener');
    loadOrders();
  };

  const confirmReception = async (order) => {
    const id = order.id || order._id;
    setRecvBusy(id);
    try {
      await orderAPI.confirmReception(id);
      toast.success('Réception confirmée. Merci !');
      setConfirmRecvFor(null);
      loadOrders();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la confirmation');
    } finally {
      setRecvBusy(null);
    }
  };

  const sendProof = async (order, file) => {
    if (!file) return;
    const id = order.id || order._id;
    setProofBusy(id);
    try {
      const fd = new FormData();
      fd.append('proof', file);
      await orderAPI.uploadPaymentProof(id, fd);
      toast.success('Reçu envoyé ! Votre paiement sera vérifié rapidement.');
      loadOrders();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur lors de l'envoi du reçu");
    } finally {
      setProofBusy(null);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: ['En attente', 'bg-yellow-100 text-yellow-800'],
      confirmed: ['Confirmée', 'bg-blue-100 text-blue-800'],
      delivered: ['Livrée', 'bg-green-100 text-green-800'],
      cancelled: ['Annulée', 'bg-red-100 text-red-800'],
      refunded: ['Remboursée', 'bg-purple-100 text-purple-800'],
    };

    const [label, cls] = map[status] || [
      status || 'Inconnu',
      'bg-gray-100 text-gray-800',
    ];

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${cls}`}>
        {label}
      </span>
    );
  };

  if (loading) return <Loading />;

  if (!orders.length) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <p className="text-gray-600 text-lg">
          Vous n&apos;avez pas encore de commandes
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Mes commandes</h2>

      {orders.map((order) => {
        const createdAt = order.createdAt || order.created;
        let formattedDate = '';
        try {
          formattedDate = createdAt
            ? format(new Date(createdAt), 'dd MMMM yyyy', { locale: fr })
            : '';
        } catch {
          formattedDate = createdAt
            ? new Date(createdAt).toLocaleDateString('fr-FR')
            : '';
        }

        return (
          <div
            key={order._id || order.orderNumber}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-lg">
                  Commande #
                  {order.orderNumber || String(order._id).slice(-6)}
                </h3>
                <p className="text-gray-600 text-sm">{formattedDate}</p>
              </div>
              {getStatusBadge(order.status || order.paymentStatus)}
            </div>

            <div className="space-y-3 mb-4">
              {(order.items || []).map((item, idx) => {
                // getDealImageUrl gère array, chaîne JSON, imageUrls, image…
                // Image de la variante choisie (couleur) : index résolu via colorImageMap du deal
                let ci = 0;
                let ciMap = item?.deal?.colorImageMap;
                if (typeof ciMap === 'string') { try { ciMap = JSON.parse(ciMap); } catch (e) { ciMap = null; } }
                if (ciMap && typeof ciMap === 'object') {
                  const toks = [];
                  if (item.variantLabel) String(item.variantLabel).split(/[/,|]/).forEach((t) => toks.push(t.trim()));
                  const at = item.attributes;
                  if (at && typeof at === 'object') { (Array.isArray(at) ? at.map((a) => a && a.value) : Object.values(at)).forEach((v) => { if (v != null) toks.push(String(v).trim()); }); }
                  for (const t of toks) { if (!t) continue; if (ciMap[t] != null) { ci = Number(ciMap[t]); break; } const k = Object.keys(ciMap).find((key) => key.toLowerCase() === t.toLowerCase()); if (k && ciMap[k] != null) { ci = Number(ciMap[k]); break; } }
                }
                const imgSrc =
                  getDealImageUrl(item.deal, ci, '') ||
                  getDealImageUrl(item.deal, 0, '') ||
                  getDealImageUrl(item, 0, '') ||
                  getPlaceholderImage(80, 80, 'Deal');

                const title =
                  item.title ||
                  item.dealId?.title ||
                  item.deal?.title ||
                  'Article';

                const qty = item.quantity || 1;
                const price = (item.price || 0) * qty;

                return (
                  <div
                    key={idx}
                    className="flex items-center space-x-4"
                  >
                    <img
                      src={imgSrc}
                      alt={title}
                      className="w-16 h-16 object-cover rounded"
                      onError={(e) => {
                        e.currentTarget.src = getPlaceholderImage(
                          80,
                          80,
                          'Deal'
                        );
                      }}
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold line-clamp-2">
                        {title}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Quantité : {qty}
                      </p>
                    </div>
                    <div className="font-bold">{price} MAD</div>
                  </div>
                );
              })}
            </div>

            <div className="border-t pt-4 flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="font-bold text-xl text-primary-600">
                  {order.total || 0} MAD
                </p>
              </div>
              <div className="flex items-center gap-4">
                {String(order.paymentMethod || '').toLowerCase() === 'cash' && order.payzoneCashToken && order.paymentStatus !== 'completed' && order.status !== 'cancelled' && (
                  <span className="text-sm font-semibold text-primary-600">
                    Code espèces&nbsp;: <span className="font-mono tracking-wider select-all">{order.payzoneCashToken}</span>
                    {order.payzoneCashReceiptUrl && (
                      <a href={order.payzoneCashReceiptUrl} target="_blank" rel="noopener noreferrer" className="ml-2 underline">reçu</a>
                    )}
                  </span>
                )}
                {String(order.paymentMethod || '').toLowerCase() === 'virement' && order.paymentStatus !== 'completed' && order.status !== 'cancelled' && (
                  <div className="w-full mt-3 mb-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FiClock className="text-amber-600" />
                      <span className="font-semibold text-amber-800">Virement en attente de validation</span>
                    </div>
                    <p className="text-gray-700 mb-3">
                      Effectuez le virement du montant exact, puis envoyez votre reçu. Votre coupon
                      (avec l'adresse et les horaires) sera disponible ici sous <strong>{VALIDATION_DELAY}</strong> après réception du virement.
                    </p>
                    <div className="space-y-1 text-gray-800">
                      <div className="flex justify-between"><span className="text-gray-600">Montant à virer :</span><span className="font-semibold">{Number(order.total || 0).toFixed(2)} MAD</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Référence (motif) :</span><span className="font-semibold">{order.orderNumber}</span></div>
                      {bankInfo && (bankInfo.bankName || bankInfo.rib || bankInfo.accountHolder) && (
                        <div className="mt-2 pt-2 border-t border-amber-200 space-y-1">
                          {bankInfo.accountHolder && <div className="flex justify-between"><span className="text-gray-600">Bénéficiaire :</span><span className="font-semibold">{bankInfo.accountHolder}</span></div>}
                          {bankInfo.bankName && <div className="flex justify-between"><span className="text-gray-600">Banque :</span><span className="font-semibold">{bankInfo.bankName}</span></div>}
                          {bankInfo.rib && <div className="flex justify-between"><span className="text-gray-600">RIB :</span><span className="font-semibold break-all">{bankInfo.rib}</span></div>}
                          {bankInfo.iban && <div className="flex justify-between"><span className="text-gray-600">IBAN :</span><span className="font-semibold break-all">{bankInfo.iban}</span></div>}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Indiquez la référence <strong>{order.orderNumber}</strong> dans le motif du virement.
                      Pas besoin de contacter le partenaire : l'adresse et les horaires apparaîtront sur votre coupon dès validation.
                    </p>
                  </div>
                )}
                {String(order.paymentMethod || '').toLowerCase() === 'virement' && order.paymentStatus !== 'completed' && order.status !== 'cancelled' && (
                  <label className="text-sm font-semibold text-indigo-600 hover:underline cursor-pointer">
                    {proofBusy === (order.id || order._id) ? 'Envoi...' : (order.paymentProof ? 'Renvoyer le reçu' : 'Envoyer le reçu de virement')}
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif" className="hidden"
                      onChange={(e) => sendProof(order, e.target.files?.[0] || null)} />
                  </label>
                )}
                {order.refundRequest && REFUND_STATUS[order.refundRequest.status] && (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${REFUND_STATUS[order.refundRequest.status].cls}`}>
                    {REFUND_STATUS[order.refundRequest.status].label}
                  </span>
                )}
                {order.refundEligible && (
                  <button
                    onClick={() => setRefundFor(order)}
                    className="text-sm font-semibold text-red-600 hover:underline"
                  >
                    Demander un remboursement
                  </button>
                )}
                {order.dispute && DISPUTE_STATUS[order.dispute.status] && (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${DISPUTE_STATUS[order.dispute.status].cls}`}>
                    {DISPUTE_STATUS[order.dispute.status].label}
                  </span>
                )}
                {!order.dispute && order.paymentStatus === 'completed' && order.status !== 'cancelled' && (
                  <button
                    onClick={() => setDisputeFor(order)}
                    className="text-sm font-semibold text-amber-700 hover:underline"
                  >
                    Ouvrir une réclamation
                  </button>
                )}
                <button
                  onClick={() => setSelectedOrder(order)}
                  className="text-primary-600 font-semibold hover:underline"
                >
                  Voir les détails
                </button>
              </div>
            </div>

            {isPhysicalOrder(order) && order.status !== 'cancelled' && order.paymentStatus === 'completed' && (
              <div className="border-t mt-4 pt-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="font-medium text-gray-700">Livraison :</span>
                    <DeliveryBadge status={order.deliveryStatus || 'pending'} />
                    {(order.carrier || order.trackingNumber) && (
                      <span className="text-gray-500">
                        {order.carrier || ''}{order.trackingNumber ? ` · ${order.trackingNumber}` : ''}
                      </span>
                    )}
                  </div>
                  {order.deliveryStatus === 'delivered' && (
                    <button
                      onClick={() => setConfirmRecvFor(order)}
                      disabled={recvBusy === (order.id || order._id)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                    >
                      <FiCheckCircle size={15} /> {recvBusy === (order.id || order._id) ? 'Confirmation...' : 'Confirmer la réception'}
                    </button>
                  )}
                </div>
                {order.deliveryStatus === 'delivered' && (
                  <p className="text-xs text-gray-400 mt-2">
                    {order.autoConfirmAt
                      ? `Sans action de votre part, la réception sera confirmée automatiquement le ${new Date(order.autoConfirmAt).toLocaleDateString('fr-FR')}${Math.ceil((new Date(order.autoConfirmAt) - new Date()) / 86400000) > 0 ? ` (dans ${Math.ceil((new Date(order.autoConfirmAt) - new Date()) / 86400000)} j)` : ''}.`
                      : 'Confirmez la réception une fois le colis bien reçu pour clôturer la commande.'}
                  </p>
                )}
              </div>
            )}

            {isDigitalOrder(order) && order.status !== 'cancelled' && order.paymentStatus === 'completed' && (
              <div className="border-t mt-4 pt-4">
                {(order.deliveryStatus === 'delivered' || order.deliveryStatus === 'received') ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="font-medium text-gray-700">Produit digital :</span>
                        <DeliveryBadge status={order.deliveryStatus} />
                      </div>
                      {order.deliveryStatus === 'delivered' && (
                        <button
                          onClick={() => setConfirmRecvFor(order)}
                          disabled={recvBusy === (order.id || order._id)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                        >
                          <FiCheckCircle size={15} /> {recvBusy === (order.id || order._id) ? 'Confirmation...' : 'Confirmer la réception'}
                        </button>
                      )}
                    </div>
                    <div className="bg-violet-50 border border-violet-100 rounded-lg p-3 space-y-2">
                      {order.digitalLink && (
                        <button onClick={() => openDigital(order)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
                          <FiDownloadCloud size={15} /> Ouvrir l'accès
                        </button>
                      )}
                      {order.digitalNote && (
                        <p className="text-sm text-gray-700 whitespace-pre-line">{order.digitalNote}</p>
                      )}
                      {order.transferWindowEndsAt && (
                        <p className="flex items-center gap-1.5 text-xs text-gray-500">
                          <FiShield size={13} /> Fenêtre de traçabilité / réclamation jusqu'au {new Date(order.transferWindowEndsAt).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="flex items-center gap-1.5 text-sm text-gray-500">
                    <FiClock size={14} /> En attente de la remise de l'accès par le vendeur.
                  </p>
                )}
              </div>
            )}

            <MerchantContactReveal order={order} />
          </div>
        );
      })}

      {/* P1-C : modal de demande de remboursement */}
      {confirmRecvFor && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/60"
          onClick={() => { if (recvBusy !== (confirmRecvFor.id || confirmRecvFor._id)) setConfirmRecvFor(null); }}>
          <style>{`@keyframes rc_pop{0%{opacity:0;transform:translateY(14px) scale(.95)}100%{opacity:1;transform:none}}@keyframes rc_ring{0%{transform:scale(.6);opacity:0}100%{transform:scale(1);opacity:1}}`}</style>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            style={{ animation: 'rc_pop .2s ease-out' }} onClick={(e) => e.stopPropagation()}>
            {/* En-tête */}
            <div className="relative px-6 pt-8 pb-6 text-center" style={{ background: 'linear-gradient(135deg,#059669 0%,#10B981 55%,#34D399 100%)' }}>
              <button type="button" onClick={() => setConfirmRecvFor(null)} aria-label="Fermer"
                className="absolute top-3 right-3 p-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/15 transition">
                <FiX size={18} />
              </button>
              <div className="mx-auto w-16 h-16 rounded-full bg-white/15 flex items-center justify-center ring-4 ring-white/20"
                style={{ animation: 'rc_ring .25s ease-out' }}>
                <FiPackage className="text-white" size={30} />
              </div>
              <h3 className="mt-3 text-xl font-bold text-white">Confirmer la réception</h3>
              <p className="text-white/85 text-sm font-mono mt-0.5">{confirmRecvFor.orderNumber}</p>
            </div>
            {/* Corps */}
            <div className="px-6 py-5">
              <p className="text-sm text-gray-600 leading-relaxed text-center">
                Vous confirmez avoir <b className="text-gray-800">bien reçu votre colis</b> ? La commande sera marquée comme <b className="text-gray-800">terminée</b>.
              </p>
              <div className="mt-3 flex items-start gap-2 text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg p-3">
                <FiAlertCircle className="shrink-0 mt-0.5 text-amber-500" size={15} />
                <span>Un souci avec votre commande ? Annulez et choisissez plutôt « Ouvrir une réclamation ».</span>
              </div>
              <div className="mt-5 flex gap-3">
                <button type="button" onClick={() => setConfirmRecvFor(null)}
                  disabled={recvBusy === (confirmRecvFor.id || confirmRecvFor._id)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                  Pas encore
                </button>
                <button type="button" onClick={() => confirmReception(confirmRecvFor)}
                  disabled={recvBusy === (confirmRecvFor.id || confirmRecvFor._id)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 shadow-sm disabled:opacity-70">
                  {recvBusy === (confirmRecvFor.id || confirmRecvFor._id)
                    ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Confirmation…</>
                    : <><FiCheckCircle size={16} /> Oui, j'ai bien reçu</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {refundFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Demander un remboursement</h3>
            <p className="text-sm text-gray-500 mb-4">
              Commande <span className="font-mono font-semibold">{refundFor.orderNumber}</span> — {Number(refundFor.total || 0).toFixed(2)} MAD
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motif</label>
            <select value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-primary-200">
              {REFUND_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label className="block text-sm font-medium text-gray-700 mb-1">Décrivez le problème</label>
            <textarea rows={4} value={refundDesc} onChange={(e) => setRefundDesc(e.target.value)}
              placeholder="Expliquez-nous ce qui s'est passé..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-primary-200" />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setRefundFor(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Annuler
              </button>
              <button type="button" disabled={refundBusy} onClick={submitRefund}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                {refundBusy ? 'Envoi...' : 'Envoyer la demande'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LOT 5d : modale de réclamation (litige) */}
      {disputeFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Ouvrir une réclamation</h3>
            <p className="text-sm text-gray-500 mb-4">
              Commande <span className="font-mono font-semibold">{disputeFor.orderNumber}</span> — {Number(disputeFor.total || 0).toFixed(2)} MAD
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motif</label>
            <select value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-primary-200">
              {DISPUTE_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label className="block text-sm font-medium text-gray-700 mb-1">Décrivez le problème</label>
            <textarea rows={4} value={disputeDesc} onChange={(e) => setDisputeDesc(e.target.value)}
              placeholder="Expliquez-nous ce qui s'est passé..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-primary-200" />
            <p className="text-xs text-gray-400 mb-4">Notre équipe examinera votre réclamation. Le vendeur en est informé.</p>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setDisputeFor(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Annuler
              </button>
              <button type="button" disabled={disputeBusy} onClick={submitDispute}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">
                {disputeBusy ? 'Envoi...' : 'Envoyer la réclamation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal détails */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between mb-4">
                <h3 className="text-xl font-semibold">
                  Détails de la commande
                </h3>
                <button onClick={() => setSelectedOrder(null)}>✕</button>
              </div>

              <div className="space-y-3">
                <p>
                  <strong>Commande :</strong>{' '}
                  {selectedOrder.orderNumber}
                </p>
                <p>
                  <strong>Statut :</strong>{' '}
                  {selectedOrder.status ||
                    selectedOrder.paymentStatus}
                </p>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Articles</h4>
                  {(selectedOrder.items || []).map((it, i) => (
                    <div
                      key={i}
                      className="flex justify-between text-sm"
                    >
                      <span>
                        {it.title ||
                          it.dealId?.title ||
                          'Article'}{' '}
                        × {it.quantity || 1}
                      </span>
                      <span className="font-semibold">
                        {(it.price || 0) *
                          (it.quantity || 1)}{' '}
                        MAD
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 flex justify-between">
                  <span className="font-bold">Total payé</span>
                  <span className="font-bold text-primary-600">
                    {selectedOrder.total} MAD
                  </span>
                </div>

                {(isPhysicalOrder(selectedOrder) || isDigitalOrder(selectedOrder)) && selectedOrder.status !== 'cancelled' && selectedOrder.paymentStatus === 'completed' && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Suivi de livraison</h4>
                    <DeliveryTimeline order={selectedOrder} productType={isDigitalOrder(selectedOrder) ? 'digital' : 'physical'} />
                    {isDigitalOrder(selectedOrder) && selectedOrder.digitalLink && (selectedOrder.deliveryStatus === 'delivered' || selectedOrder.deliveryStatus === 'received') && (
                      <button onClick={() => openDigital(selectedOrder)}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
                        <FiDownloadCloud size={15} /> Ouvrir l'accès
                      </button>
                    )}
                    {selectedOrder.deliveryStatus === 'delivered' && (
                      <button
                        onClick={() => { setConfirmRecvFor(selectedOrder); setSelectedOrder(null); }}
                        className="mt-3 ml-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
                      >
                        <FiCheckCircle size={15} /> Confirmer la réception
                      </button>
                    )}
                  </div>
                )}

                <div className="text-right">
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="px-4 py-2 bg-primary-600 text-white rounded"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
